/* FrameDex — product.js
   <variant-picker>  — accessible option buttons/radios, updates price, URL,
                       availability and add-to-cart state without full reload.
   <product-gallery> — thumbnail switching, swipe (native scroll-snap) + zoom.
   Both degrade to a usable state without JS. */
(function () {
  'use strict';

  function money(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format || window.FrameDexMoneyFormat);
    }
    return (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 }) + ' €';
  }

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variant-json]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.form = document.querySelector(`#${this.dataset.formId}`) || document.querySelector('[data-product-form]');
      this.idInput = this.form ? this.form.querySelector('[name="id"]') : null;
      this.priceTarget = document.querySelector('[data-price-block]');
      this.submit = this.form ? this.form.querySelector('[data-add-button]') : null;
      this.sku = document.querySelector('[data-variant-sku]');
      this.inventory = document.querySelector('[data-variant-inventory]');

      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input:checked, select')).map((el) => el.value);
    }

    matchVariant(options) {
      return this.variants.find((v) =>
        v.options.every((opt, i) => opt === options[i])
      );
    }

    onChange() {
      const variant = this.matchVariant(this.selectedOptions);
      this.updateAvailability();
      this.updateSelectedLabels();
      if (!variant) {
        this.setUnavailable();
        return;
      }
      this.current = variant;
      if (this.idInput) this.idInput.value = variant.id;
      this.updateURL(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateMeta(variant);
      this.updateMedia(variant);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    // Disable option values that have no available combination.
    updateAvailability() {
      const groups = Array.from(this.querySelectorAll('[data-option-position]'));
      groups.forEach((group) => {
        const pos = parseInt(group.dataset.optionPosition, 10) - 1;
        const chosen = this.selectedOptions.slice();
        group.querySelectorAll('input, option').forEach((input) => {
          const test = chosen.slice();
          test[pos] = input.value;
          const match = this.variants.find((v) =>
            v.options.every((opt, i) => (i === pos ? opt === input.value : opt === test[i] || test[i] === undefined))
          );
          const exists = this.variants.some((v) => v.options[pos] === input.value);
          const available = this.variants.some((v) => v.options[pos] === input.value && v.available);
          const label = input.closest('label') || input;
          if (!exists) {
            input.disabled = true;
            label.setAttribute('data-state', 'nonexistent');
          } else if (!available) {
            input.disabled = false;
            label.setAttribute('data-state', 'soldout');
          } else {
            input.disabled = false;
            label.setAttribute('data-state', 'available');
          }
        });
      });
    }

    // Reflect the chosen value next to each option name (e.g. "Nennwerte 100,00 €").
    updateSelectedLabels() {
      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const pos = group.dataset.optionPosition;
        const chosen = group.querySelector('input:checked, select');
        const target = this.querySelector(`[data-selected-value="${pos}"]`);
        if (chosen && target) target.textContent = chosen.value;
      });
    }

    updateURL(variant) {
      if (!variant || this.dataset.updateUrl === 'false') return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    updatePrice(variant) {
      if (!this.priceTarget) return;
      const compareEl = this.priceTarget.querySelector('[data-compare-price]');
      const priceEl = this.priceTarget.querySelector('[data-current-price]');
      if (priceEl) priceEl.textContent = money(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = money(variant.compare_at_price);
          compareEl.hidden = false;
          this.priceTarget.classList.add('is-sale');
        } else {
          compareEl.hidden = true;
          this.priceTarget.classList.remove('is-sale');
        }
      }
    }

    updateButton(variant) {
      if (!this.submit) return;
      const label = this.submit.querySelector('[data-add-label]') || this.submit;
      if (variant.available) {
        this.submit.disabled = false;
        label.textContent = this.submit.dataset.labelAdd || 'In den Warenkorb';
      } else {
        this.submit.disabled = true;
        label.textContent = this.submit.dataset.labelSoldout || 'Ausverkauft';
      }
    }

    setUnavailable() {
      if (!this.submit) return;
      const label = this.submit.querySelector('[data-add-label]') || this.submit;
      this.submit.disabled = true;
      label.textContent = this.submit.dataset.labelUnavailable || 'Nicht verfügbar';
    }

    updateMeta(variant) {
      if (this.sku) this.sku.textContent = variant.sku || '';
      if (this.inventory) {
        this.inventory.dispatchEvent(new CustomEvent('inventory:update', { detail: { variant } }));
      }
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = document.querySelector('product-gallery');
      if (gallery && typeof gallery.showMedia === 'function') {
        gallery.showMedia(variant.featured_media.id);
      }
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---- Product gallery -------------------------------------------------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.thumb));
      });

      // Lightbox: only image slides participate.
      this.zoomable = this.slides.filter((s) => s.dataset.zoomSrc);
      this.lightbox = this.querySelector('[data-lightbox]');
      if (this.dataset.zoom === 'true' && this.lightbox && this.zoomable.length) {
        this.setupLightbox();
        this.zoomable.forEach((slide) => {
          slide.addEventListener('click', () => this.openLightbox(this.zoomable.indexOf(slide)));
        });
      }
    }

    showMedia(id) {
      const target = this.slides.find((s) => s.dataset.mediaId === String(id));
      if (target) {
        this.slides.forEach((s) => s.classList.toggle('is-active', s === target));
        if (this.viewport && window.matchMedia('(max-width: 749px)').matches) {
          target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
      this.thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.thumb === String(id))));
    }

    disconnectedCallback() {
      // Remove the portaled lightbox if the gallery goes away (e.g. editor reload).
      if (this.lightbox && this.lightbox.parentElement === document.body) this.lightbox.remove();
    }

    /* ---- Lightbox modal ------------------------------------------------- */
    setupLightbox() {
      // Portal to <body> so position:fixed is relative to the viewport and not a
      // transformed ancestor (reveal animations set transform/will-change, which
      // would otherwise clip the overlay to the gallery box).
      document.body.appendChild(this.lightbox);

      this.lbImg = this.lightbox.querySelector('[data-lightbox-img]');
      this.lbStage = this.lightbox.querySelector('[data-lightbox-stage]');
      this.lbIndex = 0;
      this.imgBox = null;
      this.lastFocus = null;

      this.lightbox.addEventListener('click', (e) => {
        if (e.target.closest('[data-lightbox-prev]')) return this.step(-1);
        if (e.target.closest('[data-lightbox-next]')) return this.step(1);
        if (e.target.closest('.lightbox__img')) return this.toggleZoom(e);
        this.closeLightbox(); // backdrop / stage / close button
      });
      this.lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeLightbox();
        else if (e.key === 'ArrowLeft') this.step(-1);
        else if (e.key === 'ArrowRight') this.step(1);
        else if (e.key === 'Tab' && window.FrameDex.trapFocus) window.FrameDex.trapFocus(this.lightbox, e);
      });
      // Pan while zoomed.
      this.lbStage.addEventListener('pointermove', (e) => {
        if (this.lbImg.classList.contains('is-zoomed')) this.setOrigin(e);
      });
    }

    loadLightbox(i) {
      const total = this.zoomable.length;
      this.lbIndex = (i + total) % total;
      const slide = this.zoomable[this.lbIndex];
      this.resetZoom();
      this.lbImg.src = slide.dataset.zoomSrc;
      this.lbImg.alt = slide.dataset.zoomAlt || '';
      // Keep the main gallery in sync.
      if (slide.dataset.mediaId) this.showMedia(slide.dataset.mediaId);
    }

    openLightbox(i) {
      this.lastFocus = document.activeElement;
      this.loadLightbox(i);
      this.lightbox.hidden = false;
      document.body.classList.add('lightbox-open');
      requestAnimationFrame(() => {
        const btn = this.lightbox.querySelector('[data-lightbox-close]');
        if (btn) btn.focus();
      });
    }

    closeLightbox() {
      this.resetZoom();
      this.lightbox.hidden = true;
      document.body.classList.remove('lightbox-open');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    step(dir) { this.loadLightbox(this.lbIndex + dir); }

    resetZoom() {
      this.lbImg.classList.remove('is-zoomed');
      this.lbImg.style.transform = '';
      this.lbImg.style.transformOrigin = '';
      this.imgBox = null;
    }

    setOrigin(e) {
      const r = this.imgBox || this.lbImg.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
      const y = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
      this.lbImg.style.transformOrigin = `${x}% ${y}%`;
    }

    toggleZoom(e) {
      if (this.lbImg.classList.contains('is-zoomed')) {
        this.resetZoom();
      } else {
        this.imgBox = this.lbImg.getBoundingClientRect(); // capture pre-transform box
        this.setOrigin(e);
        this.lbImg.classList.add('is-zoomed');
        this.lbImg.style.transform = 'scale(2.4)';
      }
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ---- Quantity stepper ------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const wrap = btn.closest('[data-qty]');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const step = btn.dataset.qtyStep === 'up' ? 1 : -1;
    const min = parseInt(input.min, 10) || 1;
    input.value = Math.max(min, (parseInt(input.value, 10) || min) + step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---- Inventory message updater --------------------------------------- */
  document.addEventListener('inventory:update', (e) => {
    const el = e.target;
    const variant = e.detail.variant;
    if (!el || !variant) return;
    const threshold = parseInt(el.dataset.threshold, 10) || 0;
    let text = '';
    let state = 'in';
    if (!variant.available) { text = el.dataset.soldout || 'Ausverkauft'; state = 'out'; }
    else if (variant.inventory_management && threshold && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold) {
      text = (el.dataset.low || 'Nur noch {{ count }} verfügbar').replace('{{ count }}', variant.inventory_quantity);
      state = 'low';
    } else { text = el.dataset.instock || 'Auf Lager'; state = 'in'; }
    el.textContent = text;
    el.dataset.state = state;
  });
})();
